import axios, { isAxiosError } from "axios";
import { buildAxiosAuthenticationHeaders } from "../util/build-axios-authentication-headers";
import { clientConfigurationUrlEnvVar } from "@src/cloud-connect/feature-toggles/feature-toggles";

// Response of POST /api/v3/ns/{namespace}/opcua/client-certificate.
// Assumed field names follow the existing cert naming used across the
// clientconfiguration API. If the backend uses different names, adjust only
// mapGenerateOpcUaClientCertificateResponse below.
export interface GenerateOpcUaClientCertificateApiResponse {
  auth_type?: "OPCUA" | string;
  client_certificate_base64?: string;
  client_certificate_private_key_base64?: string;
  client_certificate_password?: string | null;
}

export interface GeneratedOpcUaClientCertificate {
  clientCertificateBase64: string;
  clientCertificatePrivateKeyBase64: string;
  clientCertificatePassword: string | null;
}

export const mapGenerateOpcUaClientCertificateResponse = (
  response: GenerateOpcUaClientCertificateApiResponse,
): GeneratedOpcUaClientCertificate => ({
  clientCertificateBase64: response.client_certificate_base64 ?? "",
  clientCertificatePrivateKeyBase64:
    response.client_certificate_private_key_base64 ?? "",
  clientCertificatePassword: response.client_certificate_password ?? null,
});

export const postGenerateOpcUaClientCertificate = async (
  namespace: string,
  hostnames: string[] = [],
) => {
  const url = clientConfigurationUrlEnvVar();
  const headers = buildAxiosAuthenticationHeaders();

  try {
    // NOTE: a JSON body is required even when empty — axios strips the
    // Content-Type header when data is undefined, and this endpoint rejects
    // requests without Content-Type: application/json (HTTP 415).
    const response =
      await axios.post<GenerateOpcUaClientCertificateApiResponse>(
        `${url}/api/v3/ns/${namespace}/opcua/client-certificate`,
        { hostnames },
        { headers },
      );

    return response;
  } catch (err) {
    if (isAxiosError(err)) {
      console.error("Axios error:", err);

      throw new Error(
        (typeof err.response?.data?.detail === "string" &&
          err.response?.data?.detail) ||
          (typeof err.response?.data?.message === "string" &&
            err.response?.data?.message) ||
          "An unexpected error occurred while generating the certificate. Please try again later.",
      );
    } else {
      throw new Error(
        "An unexpected error occurred while generating the certificate.",
      );
    }
  }
};
